import PocketBase from 'pocketbase';

// Configuration PocketBase
const pb = new PocketBase(process.env.REACT_APP_POCKETBASE_URL || 'http://localhost:8090');

/**
 * Service pour gérer les inscriptions à la newsletter
 */
export class NewsletterService {
  constructor() {
    this.collectionName = 'newletters';
  }

  /**
   * Inscrire un email à la newsletter
   * @param {string} email - L'adresse email à inscrire
   * @returns {Promise<Object>} - L'enregistrement créé
   */
  async subscribe(email) {
    try {
      // Validation de l'email côté client
      if (!this.isValidEmail(email)) {
        throw new Error('Adresse email invalide');
      }

      // Vérifier si l'email existe déjà
      const existingSubscription = await this.checkExistingEmail(email);
      if (existingSubscription) {
        throw new Error('Cette adresse email est déjà inscrite à la newsletter');
      }

      // Créer l'inscription
      const data = {
        email: email.toLowerCase().trim()
      };

      const record = await pb.collection(this.collectionName).create(data);
      
      return {
        success: true,
        data: record,
        message: 'Inscription à la newsletter réussie !'
      };
    } catch (error) {
      console.error('Erreur lors de l\'inscription à la newsletter:', error);
      
      // Gestion des erreurs spécifiques de PocketBase
      if (error.status === 400) {
        throw new Error('Données invalides. Veuillez vérifier votre adresse email.');
      } else if (error.status === 500) {
        throw new Error('Erreur serveur. Veuillez réessayer plus tard.');
      }
      
      throw error;
    }
  }

  /**
   * Vérifier si un email existe déjà dans la newsletter
   * @param {string} email - L'adresse email à vérifier
   * @returns {Promise<boolean>} - True si l'email existe déjà
   */
  async checkExistingEmail(email) {
    try {
      const records = await pb.collection(this.collectionName).getList(1, 1, {
        filter: `email = "${email.toLowerCase().trim()}"`
      });
      
      return records.items.length > 0;
    } catch (error) {
      // Si erreur 404, l'email n'existe pas
      if (error.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Obtenir toutes les inscriptions (pour l'admin)
   * @param {Object} options - Options de pagination et filtrage
   * @returns {Promise<Object>} - Liste des inscriptions
   */
  async getAll(options = {}) {
    try {
      const {
        page = 1,
        perPage = 50,
        sort = '-created'
      } = options;

      const records = await pb.collection(this.collectionName).getList(page, perPage, {
        sort
      });

      return {
        success: true,
        data: records,
        pagination: {
          page: records.page,
          perPage: records.perPage,
          totalItems: records.totalItems,
          totalPages: records.totalPages
        }
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des inscriptions:', error);
      throw error;
    }
  }

  /**
   * Désinscrire un email de la newsletter
   * @param {string} emailOrId - L'email ou l'ID de l'inscription à supprimer
   * @returns {Promise<Object>} - Résultat de la désinscription
   */
  async unsubscribe(emailOrId) {
    try {
      let recordId = emailOrId;

      // Si c'est un email, trouver l'ID correspondant
      if (emailOrId.includes('@')) {
        const records = await pb.collection(this.collectionName).getList(1, 1, {
          filter: `email = "${emailOrId.toLowerCase().trim()}"`
        });
        
        if (records.items.length === 0) {
          throw new Error('Adresse email non trouvée dans la newsletter');
        }
        
        recordId = records.items[0].id;
      }

      await pb.collection(this.collectionName).delete(recordId);
      
      return {
        success: true,
        message: 'Désinscription de la newsletter réussie'
      };
    } catch (error) {
      console.error('Erreur lors de la désinscription:', error);
      throw error;
    }
  }

  /**
   * Valider le format d'un email
   * @param {string} email - L'email à valider
   * @returns {boolean} - True si l'email est valide
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Obtenir les statistiques de la newsletter
   * @returns {Promise<Object>} - Statistiques
   */
  async getStats() {
    try {
      const totalSubscribers = await pb.collection(this.collectionName).getList(1, 1);
      
      // Inscriptions du mois en cours
      const currentMonth = new Date();
      currentMonth.setDate(1);
      currentMonth.setHours(0, 0, 0, 0);
      
      const monthlySubscribers = await pb.collection(this.collectionName).getList(1, 1, {
        filter: `created >= "${currentMonth.toISOString()}"`
      });

      return {
        success: true,
        data: {
          totalSubscribers: totalSubscribers.totalItems,
          monthlySubscribers: monthlySubscribers.totalItems
        }
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  }
}

// Instance par défaut du service
const newsletterService = new NewsletterService();
export default newsletterService;
